import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { InputAdornment, TextField } from '@mui/material';
import { PickerFileMetadata } from 'filestack-js';
import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { JSX, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ConnectedProps, connect } from 'react-redux';

import { gengql } from '@/__generated__';
import {
  CandidateQueryResponseInput,
  ExpertRequestStage,
  QueryResponseType,
} from '@/__generated__/graphql';
import { notify } from '@/actions/ui';
import Button from '@/components/Button/Button';
import CountedTextField from '@/components/CountedTextField/CountedTextField';
import CvButton from '@/components/CvButton/CvButton';
import EditDialog from '@/components/EditDialog';
import LayoutPage from '@/components/Layout/LayoutPage';
import MediaQuery from '@/components/MediaQuery';
import PhoneInput from '@/components/PhoneInput';
import { Query } from '@/components/Queries';
import ConfirmationRow from '@/components/Wizard/ConfirmationRow';
import { ERROR_CODES, hasErrorCode } from '@/core/api';
import NotFoundPage from '@/pages/NotFoundPage';
import { CV_ATTACH_ERROR, reportProfileSaveFailure } from '@/profile/profileSaveFailure';
import { updateProfile } from '@/profile/store';
import { RootState } from '@/store';
import { borderColor } from '@/theme/colors';
import { SCREEN_XS } from '@/theme/screens';
import { isPhoneValid } from '@/utils';
import { DocumentNodeResult } from '@/utils/gql';
import { capitalizer } from '@/utils/texts';

import { REQUEST_ADD_EXPERT_REQUEST_CANDIDATE } from '../store/queries';
import { effectiveStage } from '../utils/effectiveStage';
import s from './ExpertRequestAddRequest.module.scss';
import ExpertRequestAddRequestSubmitted from './ExpertRequestAddRequestSubmitted';
import { ExpertRequestNotOpenGate } from './ExpertRequestNotOpenGate';

const GET_EXPERT_REQUEST = gengql(/* GraphQL */ `
  query expertRequestAddRequestGetExpertRequest($id: String!) {
    expertRequest(id: $id) {
      id
      html_url
      name
      questions {
        id
        query
        response_type
        required
        hidden
      }
      qualifications {
        id
        query
        response_type
        required
        hidden
      }
      state
      stage
    }
  }
`);

const SET_ADDRESS = gengql(/* GraphQL */ `
  mutation expertRequestAddRequestSetAddress(
    $userId: String!
    $transport: String!
    $address: String!
    $primary: Boolean!
  ) {
    setAddress(user_id: $userId, transport: $transport, address: $address, primary: $primary) {
      address
      display
      primary
      verified
    }
  }
`);

type ExpertRequest = DocumentNodeResult<typeof GET_EXPERT_REQUEST>['expertRequest'];
type Question = NonNullable<ExpertRequest['questions']>[number];
type Qualification = NonNullable<ExpertRequest['qualifications']>[number];

// The candidate-facing form must never show hidden vetting queries (ENG-5292):
// they are dropped from the form while their already-collected answers are
// preserved server-side.
export function visibleQueries<T extends { hidden?: boolean | null }>(
  queries: readonly T[] | null | undefined
): T[] {
  return (queries ?? []).filter((q) => !q.hidden);
}

interface UpdatePhoneFormData {
  phone: string;
}

type UpdatePhoneFormErrors = FormikErrors<UpdatePhoneFormData>;

function validateUpdatePhoneForm(values: UpdatePhoneFormData): UpdatePhoneFormErrors {
  const errors: UpdatePhoneFormErrors = {};

  if (!values.phone) {
    errors.phone = 'Required';
  } else if (!isPhoneValid(values.phone)) {
    errors.phone = 'Phone must be valid.';
  }

  return errors;
}

interface UpdatePhoneProps {
  open?: boolean;
  onClose?: () => void;
  onSubmit?: () => void;
}

const updatePhoneConnector = connect(
  (state: RootState) => {
    return {
      viewer: state.viewer,
    };
  },
  {
    notify,
  }
);

const UpdatePhone = updatePhoneConnector(
  ({
    open,
    onClose,
    onSubmit,
    viewer,
    notify,
  }: UpdatePhoneProps & ConnectedProps<typeof updatePhoneConnector>): JSX.Element => {
    const [setAddress] = useMutation(SET_ADDRESS);

    const handleSubmitFinal = async (
      values: UpdatePhoneFormData,
      { setErrors }: FormikHelpers<UpdatePhoneFormData>
    ) => {
      try {
        await setAddress({
          variables: {
            userId: viewer.id,
            transport: 'phone',
            address: values.phone,
            primary: true,
          },
        });
        if (onSubmit) onSubmit();
      } catch (err: unknown) {
        if (hasErrorCode(err, ERROR_CODES.ADDRESS_ADDRESS_TAKEN)) {
          setErrors({
            phone: 'Phone number is already in use.',
          });
          return;
        }
        notify('An error occurred when updating the phone.', 'error');
        console.warn(err);
      }
    };

    const formik = useFormik<UpdatePhoneFormData>({
      initialValues: {
        phone: viewer.phone || '',
      },
      validate: validateUpdatePhoneForm,
      onSubmit: handleSubmitFinal,
    });

    return (
      <EditDialog
        open={open}
        title="What is your preferred contact number?"
        onSubmit={formik.handleSubmit}
        disableSubmit={formik.isSubmitting}
        onClose={onClose}
      >
        <p className={s.updatePhoneText}>We may contact you with a few additional questions.</p>
        <PhoneInput
          id="communicationPhone"
          type="tel"
          name="phone"
          value={formik.values.phone}
          onChange={(phone: string) => {
            formik.setFieldValue('phone', phone);
          }}
          onBlur={formik.handleBlur}
          error={formik.touched.phone && Boolean(formik.errors.phone)}
          showExampleOnError
        />
      </EditDialog>
    );
  }
);

interface FormData {
  match_experience: string;
  question_answers: CandidateQueryResponseInput[];
  qualification_responses: CandidateQueryResponseInput[];
}

type FormErrors = FormikErrors<FormData>;
type QuestionError = FormikErrors<CandidateQueryResponseInput>;

// formik thinks any key in the form error is an error even if the value is undefined so this
// function filters those elements out.
function removeEmptyElements<T>(v: T): T | undefined {
  if (v === undefined || v === null || v === '') {
    return undefined;
  }

  if (Array.isArray(v)) {
    const mapped = v.map(removeEmptyElements);
    // bit of a type hack here
    return mapped.every((v) => v === undefined) ? undefined : (mapped as T);
  }

  if (typeof v === 'object') {
    const filtered = Object.entries(v)
      .map(([k, v]) => [k, removeEmptyElements(v)])
      .filter(([_, v]) => v !== undefined);

    return filtered.length === 0 ? undefined : (Object.fromEntries(filtered) as T);
  }

  return v;
}

function validateQueryResponse(
  q: Question | Qualification,
  qa: CandidateQueryResponseInput
): QuestionError {
  const qaError: QuestionError = {};
  if (!q.required) return qaError;

  if (q.response_type === QueryResponseType.YesNo && qa.can_answer === undefined) {
    qaError.can_answer = 'Required';
  } else if (q.response_type === QueryResponseType.FreeForm && !qa.text_response) {
    qaError.text_response = 'Required';
  }

  return qaError;
}

function createValidator(
  questions: Question[],
  qualifications: Qualification[]
): (values: FormData) => FormErrors | undefined {
  return (values: FormData): FormErrors | undefined => {
    const errors: FormErrors = {};

    errors.question_answers = values.question_answers.map((qa) => {
      const q = questions.find((q) => q.id === qa.query_id);
      if (!q) return {};

      return validateQueryResponse(q, qa);
    });

    errors.qualification_responses = values.qualification_responses.map((qa) => {
      const q = qualifications.find((q) => q.id === qa.query_id);
      if (!q) return {};

      return validateQueryResponse(q, qa);
    });

    return removeEmptyElements(errors);
  };
}

export interface ExpertRequestAddRequestProps {
  expertRequestId: string;
}

const connector = connect((state: RootState) => ({ viewer: state.viewer }), {
  notify,
  updateProfile,
});

const ExpertRequestAddRequest = ({
  expertRequestId,
  viewer,
  notify,
  updateProfile,
}: ExpertRequestAddRequestProps & ConnectedProps<typeof connector>): JSX.Element | null => {
  const [done, setDone] = useState(false);
  const [resume, setResume] = useState<string | null>(null);
  const [updatePhoneOpen, setUpdatePhoneOpen] = useState(false);

  const profileId: string = viewer.profile?.id || '';
  const { data, error } = useQuery(GET_EXPERT_REQUEST, { variables: { id: expertRequestId } });
  const [requestAddExpertRequestCandidate] = useMutation(REQUEST_ADD_EXPERT_REQUEST_CANDIDATE);

  const expertRequest = data?.expertRequest;
  const questions = useMemo(() => visibleQueries(expertRequest?.questions), [expertRequest]);
  const qualifications = useMemo(
    () => visibleQueries(expertRequest?.qualifications),
    [expertRequest]
  );

  const uploadLabelText = viewer.profile?.cv_url ? 'Resume on File ✓' : 'Upload Resume (PDF, DOC)';
  const uploadButtonText = viewer.profile?.cv_url ? 'Replace' : 'Upload';

  const initialValues = useMemo(
    () => ({
      match_experience: '',
      question_answers: questions.map((q) => ({
        ...(q.response_type === QueryResponseType.YesNo ? {} : { text_response: '' }),
        query_id: q.id,
      })),
      qualification_responses: qualifications.map((q) => ({
        ...(q.response_type === QueryResponseType.YesNo ? {} : { text_response: '' }),
        query_id: q.id,
      })),
    }),
    [questions, qualifications]
  );

  const handleCVUpload = useCallback(
    async (file: PickerFileMetadata) => {
      if (!file) return;
      try {
        await updateProfile({ id: profileId, cv_url: file.url });
      } catch (err) {
        // Only name the file once it is saved, or the resume reads as attached.
        reportProfileSaveFailure(err, notify, CV_ATTACH_ERROR);
        return;
      }
      setResume(file.filename);
    },
    [profileId, updateProfile, setResume, notify]
  );

  const validate = useMemo(
    () => createValidator(questions, qualifications),
    [questions, qualifications]
  );

  const onSubmit = useCallback(
    async (values: FormData) => {
      try {
        await requestAddExpertRequestCandidate({
          variables: {
            expert_request_id: expertRequestId,
            ...values,
          },
        });
        if (viewer.phone) {
          setDone(true);
        } else {
          setUpdatePhoneOpen(true);
        }
      } catch (e: unknown) {
        let message = 'An error occurred when submitting.';
        // The page-load gate can't catch an ER that closes between load and submit;
        // the server rejects with expertrequest.not_active — show friendly copy, not raw.
        if (hasErrorCode(e, ERROR_CODES.EXPERT_REQUEST_NOT_ACTIVE)) {
          message = 'This request is no longer open. Thank you for your interest!';
        } else if (e instanceof ApolloError) {
          message = e.message;
        }
        notify(capitalizer(message), 'error');
        console.warn(e);
      }
    },
    [expertRequestId, notify, requestAddExpertRequestCandidate, viewer.phone]
  );

  const formik = useFormik<FormData>({
    enableReinitialize: true,
    initialValues,
    validate,
    onSubmit,
  });

  if (hasErrorCode(error, ERROR_CODES.EXPERT_REQUEST_NOT_FOUND)) {
    return <NotFoundPage />;
  }

  const hasQuestions = questions.length > 0;
  const hasQualifications = qualifications.length > 0;

  if (!expertRequest) {
    return null;
  }

  // Deep links (referral / auth flow) land straight on this form, bypassing the
  // public landing page's gate — so gate here too: a draft/closed ER is not
  // accepting applications (On Hold stays open — ENG-5318). Mirrors
  // ExpertRequestPublic via the shared component.
  const effective = effectiveStage(expertRequest.stage, expertRequest.state);
  if (effective === ExpertRequestStage.Draft || effective === ExpertRequestStage.Closed) {
    return <ExpertRequestNotOpenGate name={expertRequest.name} viewer={viewer} />;
  }

  if (done) {
    return <ExpertRequestAddRequestSubmitted viewer={viewer} />;
  }

  return (
    <LayoutPage hideSearch>
      <Helmet>
        <title>{expertRequest.name}</title>
      </Helmet>
      <form onSubmit={formik.handleSubmit} noValidate>
        {hasQuestions || hasQualifications ? (
          <div className={s.queries}>
            <h4 className={s.name}>{expertRequest.name}</h4>
            {hasQuestions && (
              <>
                <h3 className={s.title}>Are you able to discuss these questions?</h3>
                <h4 className={s.subTitle}>
                  If you are able to discuss these areas without conflict then there is an increased
                  likelihood you will be engaged for a consultation.
                </h4>

                <ConfirmationRow
                  separator={false}
                  separatorColor={borderColor}
                  bodyClassName={s.confirmationRowBody}
                >
                  {/* <FieldArray name="question_answers" component={Queries} queries={questions} /> */}
                  {formik.values.question_answers.map((qa, index) => {
                    const query = questions[index];
                    const touched = formik.touched.question_answers?.[index];
                    const error = (formik.errors.question_answers || [])[index] as
                      | QuestionError
                      | undefined;
                    return (
                      <Query
                        key={qa.query_id}
                        index={index}
                        name={`question_answers[${index}]`}
                        query={query.query}
                        responseType={query.response_type}
                        required={query.required}
                        value={qa}
                        onChange={({ can_answer, text_response }) => {
                          formik.setFieldValue('question_answers', [
                            ...formik.values.question_answers.slice(0, index),
                            { can_answer, text_response, query_id: query.id },
                            ...formik.values.question_answers.slice(index + 1),
                          ]);
                        }}
                        error={touched && (!!error?.can_answer || !!error?.text_response)}
                        helperText={touched && (error?.can_answer || error?.text_response)}
                        onBlur={formik.handleBlur}
                      />
                    );
                  })}
                </ConfirmationRow>
              </>
            )}
            {hasQualifications && (
              <>
                <h3 className={s.title}>Do you have these qualifications?</h3>
                <h4 className={s.subTitle}>
                  If you have these desired qualifications then there is an increased likelihood you
                  will be engaged for a consultation.
                </h4>
                <ConfirmationRow
                  separator={false}
                  separatorColor={borderColor}
                  bodyClassName={s.confirmationRowBody}
                >
                  {formik.values.qualification_responses.map((qa, index) => {
                    const query = qualifications[index];
                    const touched = formik.touched.qualification_responses?.[index];
                    const error = (formik.errors.qualification_responses || [])[index] as
                      | QuestionError
                      | undefined;
                    return (
                      <Query
                        key={qa.query_id}
                        index={index}
                        name={`qualification_responses[${index}]`}
                        query={query.query}
                        responseType={query.response_type}
                        required={query.required}
                        value={qa}
                        onChange={({ can_answer, text_response }) => {
                          formik.setFieldValue('qualification_responses', [
                            ...formik.values.qualification_responses.slice(0, index),
                            { can_answer, text_response, query_id: query.id },
                            ...formik.values.qualification_responses.slice(index + 1),
                          ]);
                        }}
                        error={touched && (!!error?.can_answer || !!error?.text_response)}
                        helperText={touched && (error?.can_answer || error?.text_response)}
                        onBlur={formik.handleBlur}
                      />
                    );
                  })}
                </ConfirmationRow>
              </>
            )}
          </div>
        ) : (
          <h3 className={s.title}>{expertRequest.name}</h3>
        )}

        <div>
          <h3 className={s.title}>Tell us why you are a match</h3>
          <h4 className={s.subTitle}>
            Help us understand your unique qualifications for this opportunity.
          </h4>

          <ConfirmationRow
            separator={false}
            separatorColor={borderColor}
            bodyClassName={s.confirmationRowBody}
          >
            <MediaQuery maxWidth={SCREEN_XS}>
              {(isMobileVersion: boolean) => (
                <CountedTextField
                  id="describeExperience"
                  fullWidth
                  multiline
                  onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    formik.setFieldValue('match_experience', event.target.value)
                  }
                  name="match_experience"
                  label="Add Note"
                  minRows={isMobileVersion ? 2 : 1}
                  maxRows={10}
                  maxLength={1000}
                />
              )}
            </MediaQuery>
          </ConfirmationRow>
        </div>
        <div>
          <h3 className={s.title}>Boost your profile by giving us your latest resume.</h3>
          <h4 className={s.subTitle}>
            You&apos;re almost done! Adding your resume significantly increases your chances of
            selection and saves time on future applications.
          </h4>
          <ConfirmationRow
            separator={false}
            separatorColor={borderColor}
            bodyClassName={s.confirmationRowBody}
          >
            <TextField
              label={uploadLabelText}
              variant="outlined"
              value={resume || ''}
              slotProps={{
                htmlInput: {
                  readOnly: true,
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CvButton label={uploadButtonText} onUpload={handleCVUpload} />
                    </InputAdornment>
                  ),
                },
              }}
              fullWidth
            />
            <h5 className={s.note}>
              Don&apos;t have your resume handy? No problem - you can add it later from your
              profile.
            </h5>
          </ConfirmationRow>
        </div>
        <Button type="submit" size="large" style={{ marginTop: 30 }} disabled={formik.isSubmitting}>
          Submit
        </Button>
      </form>
      <UpdatePhone
        onSubmit={() => setDone(true)}
        onClose={() => setUpdatePhoneOpen(false)}
        open={updatePhoneOpen}
      />
    </LayoutPage>
  );
};

export default connector(ExpertRequestAddRequest);
